// store/slices/employeeSlice.ts
import {
  employeesApi,
  Permission,
  type CreateEmployeePayload,
  type Employee,
  type UpdateEmployeePayload,
} from "@/services/employeesApi";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface EmployeeState {
  employees: Employee[];
  pagination: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  selectedEmployee: Employee | null;
  loading: boolean;
  error: string | null;
  updating: boolean;
  // Permissions
  permissions: Permission[];
  permissionsLoading: boolean;
  permissionsError: string | null;
  permissionsUpdating: boolean;
}

const initialState: EmployeeState = {
  employees: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
  },
  selectedEmployee: null,
  loading: false,
  error: null,
  updating: false,
  // Permissions
  permissions: [],
  permissionsLoading: false,
  permissionsError: null,
  permissionsUpdating: false,
};

// Thunk: Fetch all employees (paginated & search)
export const fetchEmployees = createAsyncThunk(
  "employees/fetchAll",
  async ({
    page,
    limit,
    search,
  }: {
    page: number;
    limit: number;
    search: string;
  }) => {
    const response = await employeesApi.getAll(page, limit, search);
    return response;
  },
);

// Thunk: Fetch single employee by ID
export const fetchEmployeeById = createAsyncThunk(
  "employees/fetchById",
  async (id: number) => {
    const response = await employeesApi.getById(id);
    return response.employee;
  },
);

// Thunk: Create new employee
export const createEmployee = createAsyncThunk(
  "employees/create",
  async (data: CreateEmployeePayload) => {
    const response = await employeesApi.create(data);
    return response.employee;
  },
);

// Thunk: Update employee (including isActive and password)
export const updateEmployee = createAsyncThunk(
  "employees/update",
  async ({ id, data }: { id: number; data: UpdateEmployeePayload }) => {
    const response = await employeesApi.update(id, data);
    return response.employee;
  },
);

// Thunk: Delete employee
export const deleteEmployee = createAsyncThunk(
  "employees/delete",
  async (id: number) => {
    await employeesApi.delete(id);
    return id;
  },
);

// 🔐 Thunk: Fetch employee permissions
export const fetchEmployeePermissions = createAsyncThunk(
  "employees/fetchPermissions",
  async (employeeId: number) => {
    const response = await employeesApi.getPermissions(employeeId);
    return response.permissions;
  },
);

// 🔐 Thunk: Update employee permissions
export const updateEmployeePermissions = createAsyncThunk(
  "employees/updatePermissions",
  async ({
    employeeId,
    permissions,
  }: {
    employeeId: number;
    permissions: { permissionId: number; granted: boolean }[];
  }) => {
    const response = await employeesApi.updatePermissions(
      employeeId,
      permissions,
    );
    return response.message;
  },
);

const employeeSlice = createSlice({
  name: "employees",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetSelected: (state) => {
      state.selectedEmployee = null;
      state.permissions = [];
      state.permissionsError = null;
    },
    // Reset permission states
    resetPermissions: (state) => {
      state.permissions = [];
      state.permissionsLoading = false;
      state.permissionsError = null;
      state.permissionsUpdating = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch all
    builder.addCase(fetchEmployees.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchEmployees.fulfilled, (state, action) => {
      state.loading = false;
      state.employees = action.payload.employees;
      state.pagination = action.payload.pagination;
    });
    builder.addCase(fetchEmployees.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch employees";
      state.employees = [];
    });

    // Fetch by ID
    builder.addCase(fetchEmployeeById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchEmployeeById.fulfilled, (state, action) => {
      state.loading = false;
      state.selectedEmployee = action.payload;
    });
    builder.addCase(fetchEmployeeById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch employee";
    });

    // Create
    builder.addCase(createEmployee.pending, (state) => {
      state.updating = true;
      state.error = null;
    });
    builder.addCase(createEmployee.fulfilled, (state, action) => {
      state.updating = false;
      // Optionally add to list
      state.employees.unshift(action.payload);
    });
    builder.addCase(createEmployee.rejected, (state, action) => {
      state.updating = false;
      state.error = action.error.message || "Failed to create employee";
    });

    // Update
    builder.addCase(updateEmployee.pending, (state) => {
      state.updating = true;
      state.error = null;
    });
    builder.addCase(updateEmployee.fulfilled, (state, action) => {
      state.updating = false;
      const index = state.employees.findIndex(
        (emp) => emp.id === action.payload.id,
      );
      if (index !== -1) state.employees[index] = action.payload;
      if (state.selectedEmployee?.id === action.payload.id)
        state.selectedEmployee = action.payload;
    });
    builder.addCase(updateEmployee.rejected, (state, action) => {
      state.updating = false;
      state.error = action.error.message || "Failed to update employee";
    });

    // Delete
    builder.addCase(deleteEmployee.pending, (state) => {
      state.updating = true;
      state.error = null;
    });
    builder.addCase(deleteEmployee.fulfilled, (state, action) => {
      state.updating = false;
      state.employees = state.employees.filter(
        (emp) => emp.id !== action.payload,
      );
    });
    builder.addCase(deleteEmployee.rejected, (state, action) => {
      state.updating = false;
      state.error = action.error.message || "Failed to delete employee";
    });
    // 🔐 Fetch permissions
    builder.addCase(fetchEmployeePermissions.pending, (state) => {
      state.permissionsLoading = true;
      state.permissionsError = null;
    });
    builder.addCase(fetchEmployeePermissions.fulfilled, (state, action) => {
      state.permissionsLoading = false;
      state.permissions = action.payload;
    });
    builder.addCase(fetchEmployeePermissions.rejected, (state, action) => {
      state.permissionsLoading = false;
      state.permissionsError =
        action.error.message || "Failed to fetch permissions";
    });

    // 🔐 Update permissions
    builder.addCase(updateEmployeePermissions.pending, (state) => {
      state.permissionsUpdating = true;
      state.permissionsError = null;
    });
    builder.addCase(updateEmployeePermissions.fulfilled, (state) => {
      state.permissionsUpdating = false;
    });
    builder.addCase(updateEmployeePermissions.rejected, (state, action) => {
      state.permissionsUpdating = false;
      state.permissionsError =
        action.error.message || "Failed to update permissions";
    });
  },
});

export const { clearError, resetSelected, resetPermissions } =
  employeeSlice.actions;
export default employeeSlice.reducer;
